/**
 * Collection service — batch save prints to cloud.
 * Replaces window.CollectionService.
 */
import * as renderService from './renderService';
import * as dataService from './dataService';
import type { Product, PrintItem, CloudProgress } from '../stores/appStore';
import type { Transform } from '../stores/types';
import { loadImage } from '../utils';

export async function savePrintsToCloud(params: {
  prints: PrintItem[];
  enabledProducts: Product[];
  mode: string;
  transforms: Record<string, Transform>;
  productTransforms: Record<string, Transform>;
  onProgress?: (p: CloudProgress) => void;
}): Promise<void> {
  const { prints, enabledProducts, mode, transforms, productTransforms, onProgress } = params;
  if (!prints.length) throw new Error('Нет принтов для сохранения');
  if (!enabledProducts.length) throw new Error('Нет включенных товаров');

  const totalItems = prints.length * enabledProducts.length;
  let processed = 0;

  for (const printItem of prints) {
    // Load config for EACH print from DB
    const printConfigFromDB = (await dataService.loadPrintsConfig(printItem.name)) as {
      transforms?: Record<string, Transform>;
      productTransforms?: Record<string, Transform>;
    } | null;

    const printImg = await loadImage(printItem.url);
    if (!printImg) continue;

    for (const prod of enabledProducts) {
      let productMode: string = prod.tab || mode;
      if (productMode === 'base') productMode = 'mockups';

      let tr: Transform | undefined;

      // A. From collection (current session)
      let posFromCollection: Transform | undefined;
      if (printItem.positions?.[prod.id]) {
        const savedPos = printItem.positions[prod.id];
        if ('products' in savedPos && 'mockups' in savedPos) {
          posFromCollection = (savedPos as { products: Transform; mockups: Transform })[
            productMode as 'products' | 'mockups'
          ];
        } else {
          posFromCollection = savedPos as Transform;
        }
      }

      // B. From DB
      let posFromDB: Transform | undefined;
      if (printConfigFromDB) {
        const dbTransforms =
          productMode === 'products'
            ? printConfigFromDB.productTransforms
            : printConfigFromDB.transforms;
        if (dbTransforms?.[prod.id]) {
          posFromDB = dbTransforms[prod.id];
        }
      }

      // C. Check if collection is default
      const isCollectionDefault =
        posFromCollection &&
        Math.abs(posFromCollection.x) < 0.1 &&
        Math.abs(posFromCollection.y) < 0.1 &&
        Math.abs(posFromCollection.rotation) < 0.1;

      // Priority: DB > Collection > Fallback
      if (posFromDB) {
        tr = posFromDB;
      } else if (posFromCollection && !isCollectionDefault) {
        tr = posFromCollection;
      }

      if (!tr) {
        tr = renderService.getTransformByMode(
          transforms,
          productTransforms,
          productMode,
          prod.id,
          productMode === 'products' ? 0.6 : 0.5,
        );
      }

      const productDPI = prod.dpi || 300;
      let targetWidth: number,
        targetHeight: number,
        targetMask: string | undefined,
        targetOverlay: string | undefined;

      if (productMode === 'products') {
        targetWidth = prod.width;
        targetHeight = prod.height;
        targetMask = prod.mask;
        targetOverlay = prod.overlay;
      } else {
        targetWidth = prod.mockupWidth || prod.width;
        targetHeight = prod.mockupHeight || prod.height;
        targetMask = prod.mockupMask || prod.mask;
        targetOverlay = prod.mockupOverlay || prod.overlay;
      }

      const blob = await renderService.renderMockupBlob(
        prod,
        printImg,
        tr,
        productDPI,
        targetWidth,
        targetHeight,
        {
          mimeType: 'image/png',
          maskUrl: targetMask,
          overlayUrl: targetOverlay,
        },
      );

      if (blob) {
        const categoryFolder = productMode === 'products' ? 'products' : 'mockups';
        const fileName = `${prod.defaultPrefix}-${printItem.article}.png`;
        await dataService.uploadToCloud(
          blob,
          fileName,
          printItem.article,
          categoryFolder,
          printItem.printName || printItem.name,
          prod.name,
          printItem.name,
        );
      }

      processed++;
      if (onProgress) {
        onProgress({
          total: totalItems,
          done: processed,
          current: `${printItem.article} - ${prod.name}`,
        });
      }
    }
  }
}
